<template>
  <article class="panel panel-section request-payment-panel p-bottom-4">
    <p
      class="panel-heading has-background-light has-text-weight-semibold is-size-4"
    >
      Invoices
    </p>
    <div
      class="request-payment-panel-content p-bottom-8"
      v-if="!filteredRequestLoading"
    >
      <div v-if="requestInvoices && requestInvoices.length > 0">
        <div
          v-for="invoice in requestInvoices"
          :key="`invoice-${invoice.id}`"
          class="p-bottom-8"
        >
          <div
            class="panel-item is-flex is-align-items-center"
            :class="{ 'panel-item--editable p-bottom-8': canEdit(invoice) }"
          >
            <span class="invoice p-bottom-8">{{ invoice.invoice }}</span>
            <div class="invoice-btn-actions">
              <NrTooltip v-if="canPayInvoice(invoice)" label="Pay Invoice">
                <NrIconButton
                  variant="minimal-neutral"
                  @click="
                    processClick(
                      invoice,
                      $can('manage', 'Invoice')
                        ? 'payConfirmationDialog'
                        : 'requestPaymentsDialog'
                    )
                  "
                  :aria-label="`Pay invoice ${invoice.invoice}`"
                  :icon="{ name: 'dollar-sign', type: 'regular' }"
                  :disabled="invoiceProcessing"
                  class="invoice-confirmation"
                />
              </NrTooltip>
              <NrTooltip
                v-if="canDeleteInvoice(invoice)"
                label="Delete Invoice"
              >
                <NrIconButton
                  variant="minimal-neutral"
                  @click="processClick(invoice, 'deleteConfirmationDialog')"
                  aria-label="Delete Invoice "
                  :icon="{ name: 'trash-alt', type: 'regular' }"
                  class="invoice-confirmation"
                />
              </NrTooltip>
            </div>
          </div>
          <NrModal
            :show="
              activeModal === 'payConfirmationDialog' &&
              isActiveInvoice(invoice)
            "
            v-model="selectedInvoice"
            title="Mark invoice as paid"
            confirm-button-styles="is-primary"
            primaryButtonText="Save"
            :has-secondary-button="true"
            cancel-button-styles="is-primary is-outlined"
            secondaryButtonText="Cancel"
            :disable-submit="false"
            @on-primary-click="processSubmit"
            @on-secondary-click="setActiveModal('')"
            @on-close-click="setActiveModal('')"
          >
            <p>
              {{ paidConfirmationText }}
            </p>
          </NrModal>
          <NrModal
            :show="
              activeModal === 'deleteConfirmationDialog' &&
              isActiveInvoice(invoice)
            "
            v-model="selectedInvoice"
            title="Delete invoice"
            confirm-button-styles="is-danger"
            primaryButtonText="Delete"
            :has-secondary-button="true"
            cancel-button-styles="is-primary is-outlined"
            secondaryButtonText="Cancel"
            :disable-submit="false"
            @on-primary-click="processSubmit"
            @on-secondary-click="setActiveModal('')"
            @on-close-click="setActiveModal('')"
          >
            <p>
              {{ deleteConfirmationText }}
            </p>
          </NrModal>
        </div>
      </div>
      <div v-else class="panel-item is-flex is-align-items-center">
        <span class="p-bottom-8">No invoices due</span>
      </div>
    </div>
    <NrLoadingAnimation v-else :count="3" />
  </article>
</template>

<script>
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation'
import { mapActions, mapState } from 'vuex'

export default {
  components: {
    NrIconButton,
    NrModal,
    NrTooltip,
    NrLoadingAnimation
  },
  props: {
    requestInvoices: {
      required: false,
      type: Array,
      default() {
        return []
      }
    },
    invoiceProcessing: {
      type: Boolean
    }
  },
  data() {
    return {
      selectedInvoice: []
    }
  },

  computed: {
    ...mapState(['currentUser']),
    ...mapState(['account']),
    ...mapState('request_show', {
      activeModal: (state) => state.activeModal,
      currentAction: (state) => state.currentAction,
      request: (state) => state.request,
      filteredRequestLoading: (state) => state.filteredRequestLoading
    }),

    deleteConfirmationText() {
      return `You are about to delete an invoice. This action cannot be
       undone. Do you want to continue?`
    },

    paidConfirmationText() {
      return `You are about to mark this invoice as paid. Do you want to continue?`
    }
  },

  methods: {
    ...mapActions('request_show', [
      'processInvoice',
      'setCurrentAction',
      'setActiveModal'
    ]),

    canEdit(invoice) {
      return !!invoice.outstanding
    },

    clearSelection() {
      this.setActiveModal('')
      this.selectedInvoice = []
    },

    processClick(invoice, modalName) {
      this.setActiveModal(modalName)
      this.selectedInvoice = [invoice]
    },

    processSubmit() {
      const payload = {
        invoice_id: this.selectedInvoice[0].id,
        request_id: this.request.pretty_id
      }
      if (this.activeModal === 'payConfirmationDialog') {
        payload.paid_offline = true
        payload.action = 'update'
        payload.method = 'put'
      } else {
        payload.action = 'delete'
        payload.method = 'delete'
      }
      this.processInvoice(payload).then(() => {
        this.clearSelection()
      })
    },

    canDeleteInvoice(invoice) {
      if (!invoice) {
        return false
      }

      return invoice.can_delete_invoice
    },

    canPayInvoice(invoice) {
      // Staff members can always pay invoices
      // Requester users can pay invoices only if the payment processor is activated
      return (
        invoice?.can_pay_invoice &&
        (this.$can('manage', 'Invoice') ||
          this.account.payment_processor_activated)
      )
    },

    isActiveInvoice(invoice) {
      return (
        this.selectedInvoice.length > 0 &&
        this.selectedInvoice[0].id === invoice.id
      )
    }
  }
}
</script>

<style lang="scss">
.request-payment-panel {
  .request-payment-panel-content {
    .invoice {
      margin-top: 0.5em;
    }
    .panel-item {
      margin-left: -0.75em;
      margin-right: -0.75em;
      padding: 0 0.75em;
      .invoice-btn-actions {
        margin-left: auto;
      }
      .nr-button {
        .pay-invoice.nr-icon {
          font-size: 1.5em;
        }
      }
    }
  }
}
</style>
